/**
 * Implementation Thread system for Radar.
 *
 * Groups action items into structured multi-step "threads" following
 * Thread-Based Engineering methodology: each thread has a clear start
 * (audit finding), ordered implementation steps, and an end checkpoint
 * (verification via tool re-run).
 */

import type { ActionItem, ToolKey, ToolState } from '@/types/radar'
import {
  isCrawlRetrievalBot,
  type CrawlBotResult,
} from '@/lib/tools/crawl-bots'

/* eslint-disable @typescript-eslint/no-explicit-any */

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ThreadStep {
  order: number
  title: string
  description: string
  actionIds: string[]
}

export interface ImplementationThread {
  id: string
  name: string
  description: string
  color: string
  categories: ActionItem['category'][]
  steps: ThreadStep[]
  verifyToolKeys: ToolKey[]
  actionCount: number
  estimatedEffort: 'low' | 'medium' | 'high'
}

// ── Thread Definitions ─────────────────────────────────────────────────────────

interface ThreadDef {
  id: string
  name: string
  description: string
  color: string
  categories: ActionItem['category'][]
  verifyToolKeys: ToolKey[]
  buildSteps: (
    actions: ActionItem[],
    tools: Record<ToolKey, ToolState>
  ) => ThreadStep[]
}

const THREAD_DEFS: ThreadDef[] = [
  {
    id: 'crawlability',
    name: 'Crawlability Thread',
    description:
      'Ensure AI bots can discover and access your content. This is the foundation -- nothing else works if bots cannot reach your pages.',
    color: '#008dcc',
    categories: ['crawlability'],
    verifyToolKeys: ['crawl', 'robots'],
    buildSteps: (actions, tools) => {
      const steps: ThreadStep[] = []
      let order = 1

      const botActions = actions.filter(
        a =>
          a.title.toLowerCase().includes('unblock') ||
          a.title.toLowerCase().includes('bot')
      )
      if (botActions.length > 0) {
        steps.push({
          order: order++,
          title: 'Unblock AI crawlers',
          description: buildBotStepDescription(tools),
          actionIds: botActions.map(a => a.id),
        })
      }

      const robotsActions = actions.filter(
        a =>
          a.title.toLowerCase().includes('robots.txt') || a.source === 'robots'
      )
      if (robotsActions.length > 0) {
        steps.push({
          order: order++,
          title: 'Fix robots.txt configuration',
          description:
            'Resolve syntax errors and add rules for modern AI bots. Ensure sitemap directive is present.',
          actionIds: robotsActions.map(a => a.id),
        })
      }

      const ssrActions = actions.filter(
        a =>
          a.title.toLowerCase().includes('server') ||
          a.title.toLowerCase().includes('rendering')
      )
      if (ssrActions.length > 0) {
        steps.push({
          order: order++,
          title: 'Enable server-side rendering',
          description:
            'Ensure critical content renders on the server so AI crawlers can read it without executing JavaScript.',
          actionIds: ssrActions.map(a => a.id),
        })
      }

      const remaining = actions.filter(
        a => !steps.some(s => s.actionIds.includes(a.id))
      )
      if (remaining.length > 0) {
        steps.push({
          order: order++,
          title: 'Additional crawlability fixes',
          description: remaining.map(a => a.title).join('. '),
          actionIds: remaining.map(a => a.id),
        })
      }

      return steps
    },
  },

  {
    id: 'structured-data',
    name: 'Structured Data Thread',
    description:
      'Help AI models understand your content through Schema.org markup. Structured data is how AI systems categorize and evaluate your pages.',
    color: '#8b5cf6',
    categories: ['structured-data'],
    verifyToolKeys: ['schemaAudit'],
    buildSteps: (actions, tools) => {
      const steps: ThreadStep[] = []
      let order = 1

      const schema = tools.schemaAudit?.data
      const missingTypes =
        schema?.schemaTypes?.filter((t: any) => !t.found) || []
      const foundational = [
        'Organization',
        'LocalBusiness',
        'ProfessionalService',
        'BreadcrumbList',
        'WebSite',
      ]

      const missingFoundational = missingTypes.filter((t: any) =>
        foundational.includes(t.type)
      )
      const addActions = actions.filter(
        a =>
          a.title.toLowerCase().includes('add') &&
          a.category === 'structured-data'
      )
      if (missingFoundational.length > 0 || addActions.length > 0) {
        steps.push({
          order: order++,
          title: `Add missing schema types (${missingFoundational.map((t: any) => t.type).join(', ') || 'foundational types'})`,
          description:
            'Add a business identity and WebSite entity at site level, BreadcrumbList where navigation context exists, and a schema type matching each page.',
          actionIds: addActions.map(a => a.id),
        })
      }

      const completeActions = actions.filter(
        a =>
          a.title.toLowerCase().includes('complete') ||
          a.title.toLowerCase().includes('improve')
      )
      if (completeActions.length > 0) {
        steps.push({
          order: order++,
          title: 'Complete incomplete schema types',
          description:
            'Add missing required and recommended fields to existing schema blocks. Focus on author, dateModified, publisher, and sameAs links.',
          actionIds: completeActions.map(a => a.id),
        })
      }

      const crossSignal = actions.filter(
        a =>
          a.title.toLowerCase().includes('cross-signal') ||
          a.title.toLowerCase().includes('consistency')
      )
      if (crossSignal.length > 0) {
        steps.push({
          order: order++,
          title: 'Align cross-signal consistency',
          description:
            'Ensure your brand name, description, and entity information match across meta tags, JSON-LD, and llms.txt.',
          actionIds: crossSignal.map(a => a.id),
        })
      }

      const remaining = actions.filter(
        a => !steps.some(s => s.actionIds.includes(a.id))
      )
      if (remaining.length > 0) {
        steps.push({
          order: order++,
          title: 'Additional structured data fixes',
          description: remaining.map(a => a.title).join('. '),
          actionIds: remaining.map(a => a.id),
        })
      }

      return steps
    },
  },

  {
    id: 'llm-communication',
    name: 'LLM Communication Thread',
    description:
      'Give AI models direct context about your business through llms.txt. This is how you control the narrative AI systems tell about you.',
    color: '#22c55e',
    categories: ['llm-communication'],
    verifyToolKeys: ['llms'],
    buildSteps: actions => {
      const steps: ThreadStep[] = []
      let order = 1

      const createActions = actions.filter(a =>
        a.title.toLowerCase().includes('create')
      )
      if (createActions.length > 0) {
        steps.push({
          order: order++,
          title: 'Create your llms.txt file',
          description:
            'Build a structured file at your domain root that tells AI models who you are, what you offer, and how to reference your content.',
          actionIds: createActions.map(a => a.id),
        })
      }

      const improveActions = actions.filter(a =>
        a.title.toLowerCase().includes('improve')
      )
      if (improveActions.length > 0) {
        steps.push({
          order: order++,
          title: 'Improve existing llms.txt content',
          description:
            'Strengthen weak sections with more detail, entity definitions, internal links, and a clear use policy.',
          actionIds: improveActions.map(a => a.id),
        })
      }

      const remaining = actions.filter(
        a => !steps.some(s => s.actionIds.includes(a.id))
      )
      if (remaining.length > 0) {
        steps.push({
          order: order++,
          title: 'Additional LLM communication fixes',
          description: remaining.map(a => a.title).join('. '),
          actionIds: remaining.map(a => a.id),
        })
      }

      return steps
    },
  },

  {
    id: 'content-authority',
    name: 'Content Authority Thread',
    description:
      'Optimize your content structure so AI engines extract and cite it. The difference between being mentioned and being ignored.',
    color: '#7c3aed',
    categories: ['aeo', 'answer-engine'],
    verifyToolKeys: ['aeo', 'answer'],
    buildSteps: actions => {
      const steps: ThreadStep[] = []
      let order = 1

      const speakable = actions.filter(a =>
        a.title.toLowerCase().includes('speakable')
      )
      if (speakable.length > 0) {
        steps.push({
          order: order++,
          title: 'Add SpeakableSpecification schema',
          description:
            'Tell AI assistants and AI Overviews exactly which parts of your page to read aloud and cite.',
          actionIds: speakable.map(a => a.id),
        })
      }

      const restructure = actions.filter(
        a =>
          a.title.toLowerCase().includes('restructure') ||
          a.title.toLowerCase().includes('answer-first')
      )
      if (restructure.length > 0) {
        steps.push({
          order: order++,
          title: 'Restructure content for answer-first extraction',
          description:
            'Rewrite H2 headings as questions. Front-load definitions in the first 1-2 sentences under each heading. Keep first-paragraph answers under 300 characters.',
          actionIds: restructure.map(a => a.id),
        })
      }

      const extractable = actions.filter(
        a =>
          a.title.toLowerCase().includes('extractable') ||
          a.title.toLowerCase().includes('table') ||
          a.title.toLowerCase().includes('statblock')
      )
      if (extractable.length > 0) {
        steps.push({
          order: order++,
          title: 'Add extractable data elements',
          description:
            'Convert comparison data to HTML tables. Wrap statistics in callout blocks with source attribution. Use ordered lists for step-by-step content.',
          actionIds: extractable.map(a => a.id),
        })
      }

      const citation = actions.filter(a => a.category === 'answer-engine')
      if (citation.length > 0) {
        steps.push({
          order: order++,
          title: 'Optimize for specific citation queries',
          description:
            'Target the specific questions where AI engines should cite your page. Add FAQ schema, ensure the page directly answers the query.',
          actionIds: citation.map(a => a.id),
        })
      }

      const remaining = actions.filter(
        a => !steps.some(s => s.actionIds.includes(a.id))
      )
      if (remaining.length > 0) {
        steps.push({
          order: order++,
          title: 'Additional content authority fixes',
          description: remaining.map(a => a.title).join('. '),
          actionIds: remaining.map(a => a.id),
        })
      }

      return steps
    },
  },

  {
    id: 'citation-building',
    name: 'Citation Building Thread',
    description:
      'Build the external signals that make AI models trust and recommend your brand. This is the long game -- authority takes time.',
    color: '#F90B8A',
    categories: [
      'citations',
      'community',
      'source-influence',
      'sov',
      'hallucination',
    ],
    verifyToolKeys: [
      'citation',
      'reddit',
      'sourceInfluence',
      'promptSov',
      'hallucinationCheck',
    ],
    buildSteps: actions => {
      const steps: ThreadStep[] = []
      let order = 1

      const hallucination = actions.filter(a => a.category === 'hallucination')
      if (hallucination.length > 0) {
        steps.push({
          order: order++,
          title: 'Correct AI hallucinations about your brand',
          description:
            'Fix false claims first. AI models spreading wrong information undermines everything else. Update your structured data, llms.txt, and About page with accurate facts.',
          actionIds: hallucination.map(a => a.id),
        })
      }

      const community = actions.filter(a => a.category === 'community')
      if (community.length > 0) {
        steps.push({
          order: order++,
          title: 'Build authentic community presence',
          description:
            'Engage genuinely in relevant subreddits and forums. Community signals influence AI training data. Value-first, no self-promotion.',
          actionIds: community.map(a => a.id),
        })
      }

      const sourceInfluence = actions.filter(
        a => a.category === 'source-influence'
      )
      if (sourceInfluence.length > 0) {
        steps.push({
          order: order++,
          title: 'Create category-definitive content',
          description:
            'Build the most comprehensive resource in your niche. Original research, data, and frameworks that others will reference and cite.',
          actionIds: sourceInfluence.map(a => a.id),
        })
      }

      const citations = actions.filter(a => a.category === 'citations')
      if (citations.length > 0) {
        steps.push({
          order: order++,
          title: 'Increase AI citation rate',
          description:
            'Build topical authority through comprehensive content and earn backlinks from authoritative sources. Target providers where you are not currently cited.',
          actionIds: citations.map(a => a.id),
        })
      }

      const sov = actions.filter(a => a.category === 'sov')
      if (sov.length > 0) {
        steps.push({
          order: order++,
          title: 'Grow AI share of voice',
          description:
            'Expand your presence across AI recommendation queries. Target the specific query types where competitors appear and you do not.',
          actionIds: sov.map(a => a.id),
        })
      }

      const remaining = actions.filter(
        a => !steps.some(s => s.actionIds.includes(a.id))
      )
      if (remaining.length > 0) {
        steps.push({
          order: order++,
          title: 'Additional citation building fixes',
          description: remaining.map(a => a.title).join('. '),
          actionIds: remaining.map(a => a.id),
        })
      }

      return steps
    },
  },
]

// ── Main Export ─────────────────────────────────────────────────────────────────

export function buildThreads(
  actions: ActionItem[],
  tools: Record<ToolKey, ToolState>
): ImplementationThread[] {
  return THREAD_DEFS.map(def => {
    const threadActions = actions.filter(a =>
      def.categories.includes(a.category)
    )
    if (threadActions.length === 0) return null

    const steps = def.buildSteps(threadActions, tools)
    if (steps.length === 0) return null

    const efforts = threadActions.map(a => a.effort)
    const estimatedEffort: 'low' | 'medium' | 'high' = efforts.includes('high')
      ? 'high'
      : efforts.includes('medium')
        ? 'medium'
        : 'low'

    return {
      id: def.id,
      name: def.name,
      description: def.description,
      color: def.color,
      categories: def.categories,
      steps,
      verifyToolKeys: def.verifyToolKeys,
      actionCount: threadActions.length,
      estimatedEffort,
    }
  }).filter((t): t is ImplementationThread => t !== null)
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildBotStepDescription(tools: Record<ToolKey, ToolState>): string {
  const crawl = tools.crawl?.data
  if (!crawl?.botAccess)
    return 'Run Crawl Check to verify access for current AI retrieval bots before changing robots.txt.'

  const blocked = crawl.botAccess.filter(
    (b: CrawlBotResult) => !b.allowed && isCrawlRetrievalBot(b.bot)
  )
  if (blocked.length === 0)
    return 'No current AI retrieval bots were found blocked. Verify their access with Crawl Check.'

  return `Unblock ${blocked.length} AI retrieval bot(s): ${blocked.map((b: CrawlBotResult) => b.bot).join(', ')}. Edit robots.txt to remove Disallow rules or add explicit Allow rules.`
}

/* eslint-enable @typescript-eslint/no-explicit-any */
